import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { teamApi } from '../lib/api';
import {
  ArrowLeftIcon,
  UserPlusIcon,
  TrashIcon,
  EnvelopeIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  PencilSquareIcon,
  Cog6ToothIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useAuthStore } from '../store/authStore';
import { useActiveTeamStore } from '../store/activeTeamStore';
import RoleBadge from '../components/RoleBadge';
import InstitutionSetupWizard from '../components/InstitutionSetupWizard';
import { ASSIGNABLE_ROLES, ROLE_LABELS } from '../lib/roles';
import type { TeamRole } from '../lib/roles';

const REGULATOR_LABEL: Record<string, string> = {
  FDIC: 'FDIC',
  OCC: 'OCC',
  FED: 'Federal Reserve',
  NCUA: 'NCUA',
};

export default function TeamDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const setActiveTeamId = useActiveTeamStore((s) => s.setActiveTeamId);
  const clearActiveTeam = useActiveTeamStore((s) => s.clear);
  const activeTeamId = useActiveTeamStore((s) => s.activeTeamId);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('');

  // Edit team state — owner-only inline editor for team name
  const [isEditingName, setIsEditingName] = useState(false);
  const [editName, setEditName] = useState('');

  // Institution Setup wizard (re-runnable from the detail page)
  const [showSetup, setShowSetup] = useState(false);

  // Delete-bank flow: typed-confirm + data-loss acknowledgement
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteDataCounts, setDeleteDataCounts] = useState<Record<string, number> | null>(null);

  const { data: team, isLoading } = useQuery({
    queryKey: ['team', id],
    queryFn: async () => {
      const response = await teamApi.getTeam(id!);
      return response.data;
    },
  });

  // Auto-switch the global active team to this bank when the page loads.
  // Bobby's UX call (2026-05-30): clicking into a specific bank from the
  // Organization view or the Banks list should make THIS bank the active
  // tenant — otherwise modules display data for whichever team was active
  // before, which was the muddled behavior Bobby flagged on Planters.
  useEffect(() => {
    if (id) setActiveTeamId(id);
  }, [id, setActiveTeamId]);

  const { data: invites } = useQuery({
    queryKey: ['team-invites', id],
    queryFn: async () => {
      const response = await teamApi.getTeamInvites(id!);
      return response.data;
    },
    enabled: !!id,
  });

  const inviteMutation = useMutation({
    mutationFn: ({ email, teamRole }: { email: string; teamRole?: string }) =>
      teamApi.inviteMember(id!, { email, teamRole: teamRole || undefined }),
    onSuccess: (response: any) => {
      queryClient.invalidateQueries({ queryKey: ['team', id] });
      queryClient.invalidateQueries({ queryKey: ['team-invites', id] });
      if (response.data?.emailSent === false) {
        toast.error('Invite created, but the invitation email failed to send. Please share the invite link manually.');
      } else {
        toast.success('Invite sent!');
      }
      setShowInviteModal(false);
      setInviteEmail('');
      setInviteRole('');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to send invite');
    },
  });

  const removeMemberMutation = useMutation({
    mutationFn: (userId: string) => teamApi.removeMember(id!, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team', id] });
      toast.success('Member removed');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to remove member');
    },
  });

  const updateRoleMutation = useMutation({
    mutationFn: ({ userId, teamRole }: { userId: string; teamRole: string }) =>
      teamApi.updateMemberRole(id!, userId, teamRole),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team', id] });
      toast.success('Role updated');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to update role');
    },
  });

  const updateTeamMutation = useMutation({
    mutationFn: (data: { name?: string; description?: string }) =>
      teamApi.updateTeam(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team', id] });
      // Also invalidate ALL team-list query keys so the rename flows
      // through to /teams (uses ['teams','list-unwrapped']), useTeamId
      // (uses ['teams','list-axios-response']), and any other consumer.
      // The bare ['teams'] prefix invalidates both via react-query's
      // partial-key matching.
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      toast.success('Updated');
      setIsEditingName(false);
    },
    // Errors fall through to the global axios toast handler — no need
    // to call toast.error here.
  });

  // Delete the bank. First attempt is unforced; a 409 { code: 'has_data' }
  // surfaces the record counts in the dialog, and the user must re-confirm
  // to force. Owner-only is enforced server-side too.
  const deleteTeamMutation = useMutation({
    mutationFn: (force: boolean) => teamApi.deleteTeam(id!, { force }),
    onSuccess: () => {
      toast.success('Bank deleted');
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      // If we just deleted the active tenant, drop it so useTeamId falls
      // back to the first owned team rather than pointing at a dead id.
      if (activeTeamId === id) clearActiveTeam();
      setShowDeleteModal(false);
      navigate('/teams');
    },
    onError: (error: any) => {
      const data = error?.response?.data;
      if (data?.code === 'has_data' && data?.dataCounts) {
        // Surface what would be destroyed and let the user force-confirm.
        setDeleteDataCounts(data.dataCounts);
      } else {
        toast.error(data?.error || 'Failed to delete bank');
        setShowDeleteModal(false);
      }
    },
  });

  const openDeleteModal = () => {
    setDeleteConfirmText('');
    setDeleteDataCounts(null);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (deleteConfirmText.trim() !== team?.name) return;
    // force = true once the data warning has been shown and acknowledged.
    deleteTeamMutation.mutate(deleteDataCounts !== null);
  };

  const handleStartEditName = () => {
    setEditName(team?.name || '');
    setIsEditingName(true);
  };

  const handleSaveName = () => {
    const trimmed = editName.trim();
    if (!trimmed) {
      toast.error('Name cannot be empty');
      return;
    }
    if (trimmed === team?.name) {
      setIsEditingName(false);
      return;
    }
    updateTeamMutation.mutate({ name: trimmed });
  };

  const handleCancelEditName = () => {
    setIsEditingName(false);
    setEditName('');
  };

  const handleInvite = () => {
    if (!inviteEmail) return;
    inviteMutation.mutate({ email: inviteEmail, teamRole: inviteRole });
  };

  const handleRemoveMember = (memberId: string, memberEmail: string) => {
    if (confirm(`Remove ${memberEmail} from this team?`)) {
      removeMemberMutation.mutate(memberId);
    }
  };

  const handleRoleChange = (userId: string, newRole: string) => {
    updateRoleMutation.mutate({ userId, teamRole: newRole });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!team) {
    return <div>Team not found</div>;
  }

  const currentUserMember = team.members.find((m: any) => m.userId === user?.id);
  const isAdmin = currentUserMember?.role === 'ADMIN';
  const isOwner = team.ownerId === user?.id;

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <Link
          to="/teams"
          className="inline-flex items-center text-ocean-600 hover:text-ocean-700 font-medium mb-4"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to Banks &amp; Credit Unions
        </Link>

        <div className="flex items-center justify-between">
          <div className="min-w-0 flex-1">
            {isEditingName ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  autoFocus
                  maxLength={100}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveName();
                    if (e.key === 'Escape') handleCancelEditName();
                  }}
                  className="text-2xl font-bold text-gray-900 border-b-2 border-teal-500 focus:outline-none bg-transparent px-1 py-0.5 min-w-0 flex-1"
                  placeholder="Bank or credit union name"
                />
                <button
                  onClick={handleSaveName}
                  disabled={updateTeamMutation.isPending}
                  className="inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 disabled:opacity-50"
                >
                  {updateTeamMutation.isPending ? 'Saving…' : 'Save'}
                </button>
                <button
                  onClick={handleCancelEditName}
                  disabled={updateTeamMutation.isPending}
                  className="px-3 py-1.5 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <h1 className="text-3xl font-bold text-gray-900 truncate">{team.name}</h1>
                {isOwner && (
                  <button
                    onClick={handleStartEditName}
                    className="p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                    title="Rename"
                    aria-label="Rename"
                  >
                    <PencilSquareIcon className="h-5 w-5" />
                  </button>
                )}
              </div>
            )}
            <p className="mt-2 text-gray-600">{team.members.length} members</p>
          </div>

          {isAdmin && !isEditingName && (
            <button
              onClick={() => setShowInviteModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
            >
              <UserPlusIcon className="h-5 w-5 mr-2" />
              Invite Member
            </button>
          )}
        </div>
      </div>

      {/* Institution Info */}
      <div className="bg-white rounded-lg shadow mb-8 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Institution Information</h2>
          {isOwner && (
            <button
              onClick={() => setShowSetup(true)}
              className="inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium text-primary-700 border border-primary-200 hover:bg-primary-50"
            >
              <Cog6ToothIcon className="h-4 w-4 mr-1.5" />
              {team.institutionSetupCompleted ? 'Edit Institution Setup' : 'Run Institution Setup'}
            </button>
          )}
        </div>
        <div className="space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Owner:</span>
            <span className="font-medium text-gray-900">{team.owner.email}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Primary Regulator:</span>
            <span className="font-medium text-gray-900">
              {team.primaryRegulator ? (REGULATOR_LABEL[team.primaryRegulator] || team.primaryRegulator) : '—'}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Institution Type:</span>
            <span className="font-medium text-gray-900">
              {team.primaryRegulator === 'NCUA' ? 'Credit Union' : team.primaryRegulator ? 'Bank' : '—'}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Setup Status:</span>
            {team.institutionSetupCompleted ? (
              <span className="inline-flex items-center font-medium text-green-700">
                <CheckCircleIcon className="h-4 w-4 mr-1" /> Complete
              </span>
            ) : (
              <span className="inline-flex items-center font-medium text-amber-700">
                <ExclamationTriangleIcon className="h-4 w-4 mr-1" /> Setup needed
              </span>
            )}
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Marketing Content Uploaded:</span>
            <span className="font-medium text-gray-900">{team._count?.uploads || 0}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Marketing Compliance Reports:</span>
            <span className="font-medium text-gray-900">{team._count?.reports || 0}</span>
          </div>
        </div>
      </div>

      {/* Members List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Members</h2>
        </div>
        <ul className="divide-y divide-gray-200">
          {team.members.map((member: any) => (
            <li key={member.id} className="px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="h-10 w-10 rounded-full bg-ocean-600 flex items-center justify-center text-white font-semibold">
                    {member.user.firstName?.[0] || member.user.email[0].toUpperCase()}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {member.user.firstName && member.user.lastName
                        ? `${member.user.firstName} ${member.user.lastName}`
                        : member.user.email}
                    </p>
                    <p className="text-sm text-gray-500">{member.user.email}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  {/* Owner Badge */}
                  {member.userId === team.ownerId ? (
                    <RoleBadge role="OWNER" />
                  ) : isOwner ? (
                    /* Role Dropdown (owner only) */
                    <select
                      value={member.teamRole || ''}
                      onChange={(e) => handleRoleChange(member.userId, e.target.value)}
                      disabled={updateRoleMutation.isPending}
                      className="text-xs font-medium rounded-full px-2.5 py-0.5 border border-gray-200 focus:ring-2 focus:ring-ocean-500 bg-white"
                    >
                      <option value="">Select Role</option>
                      {ASSIGNABLE_ROLES.map((r) => (
                        <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                      ))}
                    </select>
                  ) : (
                    /* Role Badge (non-owners) */
                    member.teamRole ? (
                      <RoleBadge role={member.teamRole as TeamRole} />
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        {member.role === 'ADMIN' ? 'Admin' : 'Member'}
                      </span>
                    )
                  )}

                  {/* Remove Button (only for admins, can't remove owner) */}
                  {isAdmin && member.userId !== team.ownerId && member.userId !== user?.id && (
                    <button
                      onClick={() => handleRemoveMember(member.userId, member.user.email)}
                      className="text-red-600 hover:text-red-700 p-1 rounded hover:bg-red-50"
                      title="Remove member"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  )}

                  {/* Leave Team Button (for current user, not owner) */}
                  {member.userId === user?.id && member.userId !== team.ownerId && (
                    <button
                      onClick={() => handleRemoveMember(member.userId, member.user.email)}
                      className="text-red-600 hover:text-red-700 text-sm font-medium"
                    >
                      Leave Team
                    </button>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Pending Invitations */}
      {isAdmin && invites && invites.length > 0 && (
        <div className="bg-white rounded-lg shadow mt-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Invitations</h2>
          </div>
          <ul className="divide-y divide-gray-200">
            {invites.map((invite: any) => {
              const isExpired = new Date(invite.expiresAt) < new Date();
              const isAccepted = !!invite.acceptedAt;
              const status = isAccepted ? 'accepted' : isExpired ? 'expired' : 'pending';

              return (
                <li key={invite.id} className="px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center">
                        <EnvelopeIcon className="h-5 w-5 text-gray-500" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{invite.email}</p>
                        <p className="text-sm text-gray-500">
                          Invited by {invite.invitedBy?.firstName && invite.invitedBy?.lastName
                            ? `${invite.invitedBy.firstName} ${invite.invitedBy.lastName}`
                            : invite.invitedBy?.email || 'Unknown'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      {status === 'pending' && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          <ClockIcon className="w-3 h-3 mr-1" />
                          Pending
                        </span>
                      )}
                      {status === 'accepted' && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircleIcon className="w-3 h-3 mr-1" />
                          Accepted
                        </span>
                      )}
                      {status === 'expired' && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          <XCircleIcon className="w-3 h-3 mr-1" />
                          Expired
                        </span>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Danger Zone — owner only */}
      {isOwner && (
        <div className="bg-white rounded-lg shadow mt-6 border border-red-200">
          <div className="px-6 py-4 border-b border-red-100">
            <h2 className="text-lg font-semibold text-red-700">Danger Zone</h2>
          </div>
          <div className="px-6 py-4 flex items-center justify-between">
            <div className="pr-4">
              <p className="font-medium text-gray-900">Delete this bank</p>
              <p className="text-sm text-gray-500 mt-0.5">
                Permanently removes this bank and its CRA analyses, ALERT files, board reports,
                and rate sheets, and detaches its uploads and reports. This cannot be undone.
              </p>
            </div>
            <button
              onClick={openDeleteModal}
              className="inline-flex items-center shrink-0 px-4 py-2 rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700"
            >
              <TrashIcon className="h-5 w-5 mr-2" />
              Delete Bank
            </button>
          </div>
        </div>
      )}

      {/* Institution Setup wizard (re-runnable) */}
      {showSetup && id && (
        <InstitutionSetupWizard
          isOpen={showSetup}
          teamId={id}
          onClose={() => {
            setShowSetup(false);
            queryClient.invalidateQueries({ queryKey: ['team', id] });
            queryClient.invalidateQueries({ queryKey: ['teams'] });
          }}
        />
      )}

      {/* Delete confirmation modal — typed-confirm + data-loss acknowledgement */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold text-red-700 mb-2">Delete {team.name}?</h2>
            {deleteDataCounts ? (
              <div className="mb-4 rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800">
                <p className="font-medium">This bank holds compliance data:</p>
                <ul className="mt-1 list-disc list-inside space-y-0.5">
                  {Object.entries(deleteDataCounts)
                    .filter(([, n]) => (n as number) > 0)
                    .map(([k, n]) => (
                      <li key={k}>
                        {n} {k.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toLowerCase())}
                      </li>
                    ))}
                </ul>
                <p className="mt-2">Deleting is permanent and cannot be undone. Confirm again to proceed.</p>
              </div>
            ) : (
              <p className="text-sm text-gray-600 mb-4">
                This permanently deletes the bank. Type the bank name to confirm.
              </p>
            )}
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type <span className="font-mono">{team.name}</span> to confirm
            </label>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder={team.name}
              className="w-full border border-gray-300 rounded-md px-3 py-2 mb-4"
            />
            <div className="flex gap-2">
              <button
                onClick={handleConfirmDelete}
                disabled={deleteConfirmText.trim() !== team.name || deleteTeamMutation.isPending}
                className="flex-1 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {deleteTeamMutation.isPending
                  ? 'Deleting…'
                  : deleteDataCounts
                    ? 'Delete anyway'
                    : 'Delete Bank'}
              </button>
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 border border-gray-300 px-4 py-2 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Invite Team Member</h2>
            <input
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="Email address"
              className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3"
            />
            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <select
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 mb-4 text-sm bg-white"
            >
              <option value="">No role (assign later)</option>
              {ASSIGNABLE_ROLES.map((r) => (
                <option key={r} value={r}>{ROLE_LABELS[r]}</option>
              ))}
            </select>
            <div className="flex gap-2">
              <button
                onClick={handleInvite}
                disabled={!inviteEmail || inviteMutation.isPending}
                className="flex-1 bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 disabled:opacity-50"
              >
                {inviteMutation.isPending ? 'Sending...' : 'Send Invite'}
              </button>
              <button
                onClick={() => {
                  setShowInviteModal(false);
                  setInviteEmail('');
                  setInviteRole('');
                }}
                className="flex-1 border border-gray-300 px-4 py-2 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
